import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup, {
  ToolPrepCloseContext,
} from "../../shared/Popup/ToolPrepPopup";
import ToolPrepDownloadFlowModals from "../../shared/Popup/ToolPrepDownloadFlowModals";
import ToolPrepFlatResultStage from "../../shared/Popup/ToolPrepFlatResultStage";
import ToolPrepSourceCards from "../../shared/ToolPrepSourceCards";
import ImagePickerModal from "../../../../components/popups/imagePicker/ImagePickerModal";
import { useToolPrepImageSource } from "../../hooks/useToolPrepImageSource";
import { useSyncScrollbarWidth } from "../../hooks/useSyncScrollbarWidth";
import { useToolPrepBakedApplyFlow } from "../../hooks/useToolPrepBakedApplyFlow";
import { ensureBackgroundRemoved } from "../../../../utils/ensureBackgroundRemoved";
import { exportWatermarkPrepImageBlob } from "../../lib/exportToolPrepEffectToPngBlob";
import { hasActiveWatermarkSettings } from "../../lib/toolPrepEffectActiveSettings";
import {
  TOOL_PREP_PREVIEW_BG,
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
  TOOL_PREP_RESULT_FOOTER_HINT,
  initialToolPrepPreviewBg,
} from "../../../../utils/editorEntrySession";
import { useImageNaturalSize } from "../../lib/toolPrepImageLayout";
import WatermarkControls from "./WatermarkControls";
import WatermarkPreview from "./WatermarkPreview";
import WatermarkTextFormatToolbar from "./WatermarkTextFormatToolbar";
import WatermarkImageFormatToolbar from "./WatermarkImageFormatToolbar";
import WatermarkLeftPanelOverlays from "./WatermarkLeftPanelOverlays";
import { WatermarkToolbarOverlayProvider } from "./WatermarkToolbarOverlayContext";
import { DEFAULT_WATERMARK_SETTINGS } from "./watermarkSettings";
import "./WatermarkToolPrep.css";

const WATERMARK_THANK_YOU_COPY = {
  titleDownloading: "Downloading…",
  titleComplete: "Download complete",
  leadDownloading: "Please wait — we’re saving your file to this device.",
  leadComplete: "Your file is saved on this computer or phone. You can find it in your Downloads folder.",
  previewAlt: "Watermarked image download",
  primaryActionLabel: "Add new image",
};

/**
 * Watermark Maker prep — upload + Remove BG + watermark controls + Apply/Result flow.
 */
const WatermarkToolPrep = ({
  originalImageUrl = null,
  imageFile = null,
  removeBgDefault = false,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const openedFromEditor = useContext(ToolPrepCloseContext) === "editor";
  const [removeBg, setRemoveBg] = useState(Boolean(removeBgDefault));
  const [cutoutUrl, setCutoutUrl] = useState(cachedCutoutUrl || null);
  const [creationId, setCreationId] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [doneBusy, setDoneBusy] = useState(false);
  const [watermark, setWatermark] = useState(DEFAULT_WATERMARK_SETTINGS);
  const [compareMode, setCompareMode] = useState("preview");
  const bgRequestRef = useRef(null);
  const scrollBodyRef = useRef(null);
  const resetAppliedRef = useRef(() => {});

  const {
    sourceImageUrl,
    resolvedFile,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
    sourceFileName,
    sourceFileSizeLabel,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: bgLoading || doneBusy,
    onSourceChange: () => {
      setCutoutUrl(null);
      setCreationId(null);
      setRemoveBg(false);
      setBgError(null);
      setCompareMode("preview");
      setWatermark(DEFAULT_WATERMARK_SETTINGS);
      resetAppliedRef.current();
    },
  });

  const {
    applied: watermarkApplied,
    busy: watermarkBusy,
    resultUrl: watermarkedPreviewUrl,
    applyError: watermarkApplyError,
    setApplyError: setWatermarkApplyError,
    apply: applyWatermark,
    resetApplied,
    downloadBusy,
    handleDownload,
    downloadFlow,
  } = useToolPrepBakedApplyFlow({
    sourceFileName,
    fallbackDownloadName: "watermark.png",
    toolKey: "watermark_maker",
    shareTitle: "Watermark Maker",
    shareText: "I added a watermark to my photo with LazyStudio's Watermark Maker!",
    thankYouCopy: WATERMARK_THANK_YOU_COPY,
  });

  resetAppliedRef.current = resetApplied;

  const mergeWatermark = useCallback(
    (updates) => {
      resetApplied();
      setWatermarkApplyError(null);
      setWatermark((prev) => ({ ...prev, ...updates }));
    },
    [resetApplied, setWatermarkApplyError]
  );

  useEffect(() => {
    if (cachedCutoutUrl) setCutoutUrl(cachedCutoutUrl);
  }, [cachedCutoutUrl]);

  useSyncScrollbarWidth(scrollBodyRef, [
    sourceImageUrl,
    removeBg,
    watermark,
    watermarkApplied,
  ]);

  const runBackgroundRemoval = useCallback(async () => {
    if (cutoutUrl?.startsWith("blob:") || cutoutUrl?.startsWith("data:")) {
      return { cutoutUrl, creationId };
    }

    const requestId = Symbol("bg");
    bgRequestRef.current = requestId;
    setBgLoading(true);
    setBgError(null);

    try {
      const token = await getToken();
      const { cutoutUrl: nextUrl, creationId: cid } =
        await ensureBackgroundRemoved({
          token,
          imageFile: resolvedFile,
          imageUrl: sourceImageUrl,
          cachedCutoutUrl: cutoutUrl || cachedCutoutUrl,
        });
      if (bgRequestRef.current !== requestId) return null;
      setCutoutUrl(nextUrl);
      if (cid != null) setCreationId(cid);
      onCutoutReady?.(nextUrl);
      return { cutoutUrl: nextUrl, creationId: cid ?? creationId };
    } catch (err) {
      if (bgRequestRef.current === requestId) {
        setBgError(err?.message || "Failed to remove background");
      }
      throw err;
    } finally {
      if (bgRequestRef.current === requestId) {
        setBgLoading(false);
      }
    }
  }, [
    cachedCutoutUrl,
    creationId,
    cutoutUrl,
    getToken,
    onCutoutReady,
    resolvedFile,
    sourceImageUrl,
  ]);

  const handleRemoveBgToggle = async () => {
    if (!sourceImageUrl) return;
    const next = !removeBg;
    setRemoveBg(next);
    if (!next) {
      setBgError(null);
      return;
    }
    try {
      await runBackgroundRemoval();
    } catch {
      setRemoveBg(false);
    }
  };

  const previewImageUrl =
    removeBg && cutoutUrl ? cutoutUrl : sourceImageUrl;
  const viewingOriginal = compareMode === "original";
  const stageImageUrl = viewingOriginal ? sourceImageUrl : previewImageUrl;
  const resultPreviewBg = removeBg
    ? initialToolPrepPreviewBg()
    : TOOL_PREP_PREVIEW_BG.ORIGINAL;
  // Cutout sits on the white Preview card. The original photo keeps the checker.
  const stagePreviewBg =
    removeBg && !viewingOriginal ? resultPreviewBg : TOOL_PREP_PREVIEW_BG.TRANSPARENT;
  const frameNaturalSize = useImageNaturalSize(previewImageUrl);

  const buildExport = useCallback(() => {
    const url = previewImageUrl;
    const wm = watermark;
    return () => exportWatermarkPrepImageBlob(url, { watermark: wm });
  }, [previewImageUrl, watermark]);

  const handleApplyWatermark = async () => {
    if (
      !previewImageUrl ||
      watermarkBusy ||
      !hasActiveWatermarkSettings(watermark)
    ) {
      return;
    }
    await applyWatermark(buildExport());
  };

  const handleApplyToEditor = () => {
    const imageUrl = removeBg && cutoutUrl ? cutoutUrl : sourceImageUrl;
    if (!imageUrl || !hasActiveWatermarkSettings(watermark)) return;
    onDone({
      mode: "watermark_maker",
      removeBg,
      previewBg: resultPreviewBg,
      imageUrl,
      creationId: removeBg ? creationId : null,
      watermark,
    });
  };

  const handleAdjustWatermark = () => {
    setWatermarkApplyError(null);
    resetApplied();
  };

  const handleDone = async () => {
    if (!sourceImageUrl) return;
    setDoneBusy(true);
    try {
      if (watermarkApplied && watermarkedPreviewUrl) {
        onDone({
          imageUrl: watermarkedPreviewUrl,
          removeBg,
          previewBg: resultPreviewBg,
          creationId: removeBg ? creationId : null,
          effectBaked: true,
        });
        return;
      }

      let imageUrl = sourceImageUrl;
      let doneCreationId = creationId;
      if (removeBg) {
        if (cutoutUrl) {
          imageUrl = cutoutUrl;
        } else {
          const removed = await runBackgroundRemoval();
          imageUrl = removed?.cutoutUrl;
          doneCreationId = removed?.creationId ?? doneCreationId;
        }
      }
      if (!imageUrl) {
        throw new Error("No image ready");
      }
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:")
      ) {
        throw new Error(
          "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
        );
      }
      onDone({
        mode: "watermark_maker",
        removeBg,
        previewBg: resultPreviewBg,
        imageUrl,
        creationId: removeBg ? doneCreationId : null,
        watermark,
      });
    } catch (err) {
      setBgError(err?.message || "Could not finish setup");
    } finally {
      setDoneBusy(false);
    }
  };

  const controlsLocked =
    watermarkApplied || watermarkBusy || bgLoading || doneBusy || downloadBusy;

  const canApplyWatermark =
    Boolean(sourceImageUrl) &&
    Boolean(previewImageUrl) &&
    hasActiveWatermarkSettings(watermark) &&
    !watermarkBusy;

  const configureFooterHint = !sourceImageUrl
    ? "Upload an image to start adding a watermark."
    : !hasActiveWatermarkSettings(watermark)
      ? "Add text or a logo watermark, then click Apply Watermark."
      : "Adjust your watermark, then click Apply Watermark.";

  const leftContent = (
    <div className="wtp-panel">
      <div className="wtp-sticky-top">
        <ToolPrepSourceCards
          selectedKey={compareMode}
          onSelect={setCompareMode}
          originalImageUrl={sourceImageUrl}
          previewImageUrl={previewImageUrl}
          removeBgActive={removeBg}
          cutoutPending={bgLoading && !cutoutUrl}
          onUploadClick={openUploadPicker}
          onRemove={clearSource}
          uploadProgress={uploadProgress}
          sourceName={sourceFileName}
          sourceSizeLabel={sourceFileSizeLabel}
          watermark={watermark}
          actionsDisabled={bgLoading || doneBusy || downloadBusy}
          removeBgControls={{
            active: removeBg,
            onToggle: handleRemoveBgToggle,
            disabled:
              bgLoading || !sourceImageUrl || watermarkApplied || watermarkBusy,
            locked: watermarkApplied,
            bio: "Optional — remove the backdrop before adding a watermark.",
            error: bgError,
          }}
        />
      </div>

      <div className="wtp-controls tool-prep-scroll" ref={scrollBodyRef}>
        <WatermarkControls
          settings={watermark}
          onChange={mergeWatermark}
          hideFormatControls
          disabled={!sourceImageUrl || controlsLocked}
        />
      </div>
    </div>
  );

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <WatermarkToolbarOverlayProvider leftPanelSelector='.tool-prep-popup[data-tool="watermark_maker"] .tool-prep-popup__left'>
        <ToolPrepPopup
          toolKey="watermark_maker"
          title="Watermark Maker"
          left={leftContent}
          preview={
            <div className="wtp-preview-shell">
              {!watermarkApplied ? (
                <>
                  <WatermarkTextFormatToolbar
                    watermark={watermark}
                    onChange={mergeWatermark}
                    visible={Boolean(previewImageUrl)}
                    disabled={!sourceImageUrl || controlsLocked}
                    anchorSelector='.tool-prep-popup[data-tool="watermark_maker"] .tool-prep-popup__right'
                  />
                  <WatermarkImageFormatToolbar
                    watermark={watermark}
                    onChange={mergeWatermark}
                    visible={Boolean(previewImageUrl)}
                    disabled={!sourceImageUrl || controlsLocked}
                    toolKey="watermark_maker"
                  />
                  <WatermarkPreview
                    imageUrl={stageImageUrl}
                    loading={bgLoading || watermarkBusy}
                    loadingLabel={
                      watermarkBusy ? "Applying watermark…" : "Removing background…"
                    }
                    previewBg={stagePreviewBg}
                    watermark={watermark}
                    onWatermarkChange={mergeWatermark}
                    compareMode={compareMode}
                    emptyLabel={
                      uploadProgress != null && !stageImageUrl
                        ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                        : undefined
                    }
                  />
                </>
              ) : (
                <ToolPrepFlatResultStage
                  className="wtp-flat-result"
                  resultImageUrl={watermarkedPreviewUrl}
                  previewBg={resultPreviewBg}
                  applying={watermarkBusy}
                  applyingLabel="Applying watermark…"
                  resultAlt="Watermarked result"
                  frameNaturalSize={frameNaturalSize}
                  softWatermarkToolKey="watermark_maker"
                />
              )}
            </div>
          }
          onDone={
            openedFromEditor
              ? handleApplyToEditor
              : watermarkApplied
                ? handleDone
                : handleApplyWatermark
          }
          onCancel={onCancel}
          confirmClose={Boolean(sourceImageUrl)}
          onDownload={
            openedFromEditor
              ? null
              : watermarkApplied
                ? () => handleDownload(buildExport())
                : null
          }
          onOpenInEditor={
            openedFromEditor ? null : watermarkApplied ? handleDone : null
          }
          onSecondaryAction={
            openedFromEditor ? null : watermarkApplied ? handleAdjustWatermark : null
          }
          secondaryActionLabel="Adjust watermark"
          busy={
            openedFromEditor
              ? bgLoading
              : watermarkBusy || doneBusy || bgLoading || downloadBusy
          }
          busyLabel="Applying watermark…"
          showDone={openedFromEditor || !watermarkApplied}
          doneDisabled={!canApplyWatermark}
          doneLabel="Apply Watermark"
          footerActionOrder={
            watermarkApplied
              ? ["cancel", "secondary", "download", "openInEditor"]
              : ["cancel", "primary"]
          }
          openInEditorLabel="Open in Editor"
          footerHint={
            watermarkApplyError ||
            (watermarkApplied
              ? TOOL_PREP_RESULT_FOOTER_HINT
              : configureFooterHint)
          }
          footerHintClassName={
            watermarkApplyError ? "tool-prep-popup__footer-hint--error" : ""
          }
        />
        <WatermarkLeftPanelOverlays
          settings={watermark}
          onChange={mergeWatermark}
          disabled={!sourceImageUrl || controlsLocked}
          positionStageSelector=".transform-preview__canvas"
        />
      </WatermarkToolbarOverlayProvider>
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey="watermark_maker"
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default WatermarkToolPrep;
